import { ref, watch, type Ref } from 'vue'
import { storeToRefs } from 'pinia'
import {
  createSSBTIntegratorParent,
  createPOSIntegratorParent,
  type IntegratorProductTarget,
  type SettingsUpdateData,
  type IntegratorDenyBettingData,
  type DomEventData,
} from '@nsftx/seven-client-sdk-integrator'
import type { POSLayoutModeEventData, POSWidgetVisibilityEventData } from '@nsftx/seven-client-sdk-integrator/pos'
import { logService } from '@nsftx/seven-client-retail-utils'
import { useIntegratorPluginOptions } from '#src/composables/useIntegratorPluginOptions'
import type {
  IntegratorChildren,
  IntegratorConfig,
  IntegratorConfigChildren,
  IntegratorInstance,
  IntegratorEventHandlers,
  IntegratorEventHandlersKey,
  ParentType,
} from '#src/types/index'
import { useIntegratorStore } from '#src/stores/useIntegratorStore'

const INTEGRATOR_CREATE_MAP: Record<ParentType, (config: IntegratorConfig) => IntegratorInstance> = {
  ssbt: createSSBTIntegratorParent as (config: IntegratorConfig) => IntegratorInstance,
  pos: createPOSIntegratorParent as (config: IntegratorConfig) => IntegratorInstance,
}
const IGNORED_EVENTS: IntegratorEventHandlersKey[] = [
  'onDomEvent',
  'onNavigationRequested',
  'onNavigationChanged',
]
const LOG_PREFIX = '[useIntegrator]'

let integrator: IntegratorInstance | undefined
let tokenUpdate: ReturnType<typeof watch> | undefined

const onChildLoaded = ref<(() => void) | null>(null)

const createOverriddenEventHandlers = (configEventHandlers: IntegratorEventHandlers) => {
  const configOnLoaded = configEventHandlers?.onLoaded

  return {
    onLoaded: () => {
      const childLoadedCallback = onChildLoaded.value
      if (!childLoadedCallback) {
        return
      }

      configOnLoaded?.()
      childLoadedCallback()
    },
  }
}

const createIgnoredEventHandlers = (configEventHandlers: IntegratorEventHandlers) =>
  Object.fromEntries(
    IGNORED_EVENTS.filter((eventName) => configEventHandlers?.[eventName]).map((eventName) => [
      eventName,
      (args: unknown) => {
        const { activeChildId, children } = useIntegratorStore()
        const activeChild = children.find(({ childId }) => childId === activeChildId)

        if (!activeChild?.isAwake) {
          logService.debug(`${LOG_PREFIX} Event skipped while child is snoozed.`, {
            code: 'INTR_EVENT_SKIPPED',
            details: {
              childId: activeChildId,
              event: eventName,
            },
          })
          return
        }

        const eventHandler = configEventHandlers[eventName] as (args: unknown) => void
        eventHandler(args)
      },
    ]),
  )

const createIntegratorEvents = (configEventHandlers: IntegratorEventHandlers) => {
  const overriddenEventHandlers = createOverriddenEventHandlers(configEventHandlers)
  const ignoredEventHandlers = createIgnoredEventHandlers(configEventHandlers)

  return {
    ...configEventHandlers,
    ...overriddenEventHandlers,
    ...ignoredEventHandlers,
  }
}

export function useIntegrator() {
  const integratorStore = useIntegratorStore()

  const createIntegrator = (
    config: IntegratorConfig,
    tokenRef: Readonly<Ref<string | undefined>>,
  ) => {
    const { isDebug } = storeToRefs(integratorStore)
    const { setChildren } = integratorStore
    const { options } = useIntegratorPluginOptions()

    if (integrator) {
      throw new Error('Integrator is already initialized.')
    }

    const type = options.type
    if (!type) {
      throw new Error('Integrator plugin is not installed.')
    }

    const finalConfig = {
      ...config,
      events: config.events ? createIntegratorEvents(config.events) : undefined,
    }

    const createIntegratorFn = INTEGRATOR_CREATE_MAP[type]
    if (!createIntegratorFn) {
      throw new Error(`Unsupported type ${type}.`)
    }

    isDebug.value = config.context.debug ?? false

    try {
      integrator = createIntegratorFn(finalConfig)
      setChildren(createIntegratorChildren(config.children))

      tokenUpdate = watch(tokenRef, (token) => {
        if (token) {
          integrator?.setAuth({ token })
        }
      })

      return integrator
    } catch (error) {
      throw error
    }
  }

  const getIntegrator = () => {
    if (!integrator) {
      throw new Error('Integrator is not initialized.')
    }

    return integrator
  }

  const awake = (childId: string) => {
    integratorStore.activeChildId = childId
    getIntegrator().awakeUI(childId)
  }

  const snooze = (childId: string) => {
    if (integratorStore.activeChildId === childId) {
      integratorStore.activeChildId = null
    }
    getIntegrator().snoozeUI(childId)
  }

  const getWallets = (options?: { timeout?: number }) => {
    const instance = getIntegrator()

    if (!('getWallets' in instance) || typeof instance.getWallets !== 'function') {
      throw new Error('getWallets is not supported for the current integrator type.')
    }

    return instance.getWallets(options)
  }

  const updateSettings = (settings: SettingsUpdateData) => getIntegrator().updateSettings(settings)

  const allowBetting = (targetProduct?: IntegratorProductTarget) =>
    getIntegrator().allowBetting(targetProduct)

  const denyBetting = (denyBettingData?: IntegratorDenyBettingData) =>
    getIntegrator().denyBetting(denyBettingData)

  const rebet = (
    feBetslipId: string,
    betIds: string[],
    targetProduct?: IntegratorProductTarget,
  ) => getIntegrator().reBet({ feBetslipId, betIds }, targetProduct)

  const cancelBets = (
    feBetslipId: string,
    betIds: string[],
    targetProduct?: IntegratorProductTarget,
  ) => getIntegrator().cancelBets({ feBetslipId, betIds }, targetProduct)

  const createIntegratorChildren = (children: IntegratorConfigChildren): IntegratorChildren =>
    children.map(({ childId }) => ({
      childId,
      isLoaded: false,
      isAwake: false,
    }))

  const teardownIntegrator = () => {
    const { setChildren } = integratorStore
    integrator = undefined
    if (tokenUpdate) {
      tokenUpdate()
      tokenUpdate = undefined
    }
    setChildren([])
  }

  const emitWidgetVisibilityChange = (widgetVisibilityData: POSWidgetVisibilityEventData) => {
    const instance = getIntegrator()

    if (!('emitWidgetVisibilityChange' in instance) || typeof instance.emitWidgetVisibilityChange !== 'function') {
      throw new Error('emitWidgetVisibilityChange is not supported for the current integrator type.')
    }

    instance.emitWidgetVisibilityChange(widgetVisibilityData)
  }

  const emitLayoutModeChange = (layoutModeData: POSLayoutModeEventData) => {
    const instance = getIntegrator()

    if (!('emitLayoutModeChange' in instance) || typeof instance.emitLayoutModeChange !== 'function') {
      throw new Error('emitLayoutModeChange is not supported for the current integrator type.')
    }

    instance.emitLayoutModeChange(layoutModeData)
  }

  const emitDomEvent = (eventData: DomEventData) => getIntegrator().emitDomEvent(eventData)

  return {
    createIntegrator,
    getIntegrator,
    createIntegratorChildren,
    awake,
    snooze,
    getWallets,
    emitWidgetVisibilityChange,
    emitLayoutModeChange,
    updateSettings,
    allowBetting,
    denyBetting,
    rebet,
    cancelBets,
    teardownIntegrator,
    onChildLoaded,
    emitDomEvent
  }
}
